import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row, Col, Card, Button, Form, Table, Badge, Modal,
  Spinner, Nav, Tab, InputGroup, Alert,
} from 'react-bootstrap';
import { Plus, Trash, PencilSquare, ArrowRepeat, Stars, BarChart, ListUl } from 'react-bootstrap-icons';
import { Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import PageLayout from '../../layouts/PageLayout';
import { randomRewardAPI } from '../../services';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function formatVND(n) {
  return Number(n || 0).toLocaleString('vi-VN');
}
function timeAgo(d) {
  try { return formatDistanceToNow(new Date(d), { addSuffix: true, locale: vi }); }
  catch { return '—'; }
}
function newTier(id = '') {
  return { id: id || `tier_${Math.random().toString(36).slice(2, 7)}`, label: '', weight: 1, reward: { type: 'PERCENTAGE', value: 10 } };
}
function expectedMean(tiers) {
  const totalW = tiers.reduce((s, t) => s + Number(t.weight || 0), 0);
  if (!totalW) return 0;
  return tiers.reduce((s, t) => s + (Number(t.weight) / totalW) * Number(t.reward?.value || 0), 0);
}

const EMPTY_FORM = {
  context_key: '',
  name: '',
  description: '',
  tiers: [newTier(), newTier(), newTier()],
  expected_mean: null,
  hard_min: null,
  hard_max: null,
  daily_budget: null,
  high_tier_caps: [],
  is_active: true,
};

// ─────────────────────────────────────────────────────────────────────────────
// Tier Editor (used in create/edit form)
// ─────────────────────────────────────────────────────────────────────────────
function TierEditor({ tiers, onChange }) {
  const set = (idx, field, value) => {
    const next = tiers.map((t, i) => {
      if (i !== idx) return t;
      if (field === 'value' || field === 'type') {
        return { ...t, reward: { ...t.reward, [field]: field === 'value' ? Number(value) : value } };
      }
      return { ...t, [field]: field === 'weight' ? Number(value) : value };
    });
    onChange(next);
  };
  const addRow = () => onChange([...tiers, newTier()]);
  const removeRow = (idx) => onChange(tiers.filter((_, i) => i !== idx));

  const totalW = tiers.reduce((s, t) => s + Number(t.weight || 0), 0);

  return (
    <div>
      <Table size="sm" responsive style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th>ID</th><th>Nhãn</th><th>Loại</th><th>Giá trị</th>
            <th>Weight</th><th>Xác suất</th><th></th>
          </tr>
        </thead>
        <tbody>
          {tiers.map((t, i) => {
            const prob = totalW ? (Number(t.weight) / totalW) * 100 : 0;
            return (
              <tr key={i}>
                <td>
                  <Form.Control size="sm" value={t.id}
                    onChange={(e) => set(i, 'id', e.target.value)}
                    style={{ fontFamily: 'monospace', fontSize: 12 }} />
                </td>
                <td>
                  <Form.Control size="sm" value={t.label || ''} placeholder="Giải nhì"
                    onChange={(e) => set(i, 'label', e.target.value)} />
                </td>
                <td>
                  <Form.Select size="sm" value={t.reward.type} onChange={(e) => set(i, 'type', e.target.value)}>
                    <option value="PERCENTAGE">%</option>
                    <option value="FIXED_AMOUNT">VND</option>
                    <option value="POINTS">điểm</option>
                  </Form.Select>
                </td>
                <td>
                  <Form.Control size="sm" type="number" min={0}
                    value={t.reward.value} onChange={(e) => set(i, 'value', e.target.value)} />
                </td>
                <td>
                  <Form.Control size="sm" type="number" min={0} step="0.1"
                    value={t.weight} onChange={(e) => set(i, 'weight', e.target.value)} />
                </td>
                <td><Badge bg="info">{prob.toFixed(2)}%</Badge></td>
                <td>
                  <Button size="sm" variant="outline-danger" onClick={() => removeRow(i)}
                    disabled={tiers.length <= 1}><Trash size={12} /></Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <div className="d-flex justify-content-between align-items-center mt-2">
        <Button size="sm" variant="outline-secondary" onClick={addRow}>
          <Plus size={12} className="me-1" /> Thêm tier
        </Button>
        <div className="text-muted small">
          Mean kỳ vọng: <strong>{expectedMean(tiers).toFixed(2)}</strong>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AI Suggest Modal
// ─────────────────────────────────────────────────────────────────────────────
function AiSuggestModal({ show, onHide, onApply }) {
  const [body, setBody] = useState({
    expected_mean: 15, hard_min: 0, hard_max: 50,
    reward_type: 'PERCENTAGE', tier_count: 5, vibe: '',
    context_description: 'Discount cho chương trình review Google Maps',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setBody((b) => ({ ...b, [k]: v }));

  const handleSuggest = async () => {
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        context_description: body.context_description,
        reward_type: body.reward_type,
        expected_mean: Number(body.expected_mean),
        hard_min: Number(body.hard_min),
        hard_max: Number(body.hard_max),
      };
      const tc = Number(body.tier_count);
      if (Number.isFinite(tc) && tc >= 2) payload.tier_count = tc;
      if (body.vibe?.trim()) payload.vibe = body.vibe.trim();
      const res = await randomRewardAPI.aiSuggest(payload);
      setResult(res);
    } catch (e) {
      toast.error(e?.message || 'AI suggest lỗi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title><Sparkles size={18} className="me-2" />AI Suggest Tiers</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-2 mb-3">
          <Col md={3}>
            <Form.Label className="small">Loại reward</Form.Label>
            <Form.Select size="sm" value={body.reward_type} onChange={(e) => set('reward_type', e.target.value)}>
              <option value="PERCENTAGE">%</option>
              <option value="FIXED_AMOUNT">VND</option>
              <option value="POINTS">điểm</option>
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label className="small">Mean kỳ vọng</Form.Label>
            <Form.Control size="sm" type="number" value={body.expected_mean}
              onChange={(e) => set('expected_mean', e.target.value)} />
          </Col>
          <Col md={2}>
            <Form.Label className="small">Hard min</Form.Label>
            <Form.Control size="sm" type="number" value={body.hard_min}
              onChange={(e) => set('hard_min', e.target.value)} />
          </Col>
          <Col md={2}>
            <Form.Label className="small">Hard max</Form.Label>
            <Form.Control size="sm" type="number" value={body.hard_max}
              onChange={(e) => set('hard_max', e.target.value)} />
          </Col>
          <Col md={2}>
            <Form.Label className="small">Số tier</Form.Label>
            <Form.Control size="sm" type="number" min={2} max={20} value={body.tier_count}
              onChange={(e) => set('tier_count', e.target.value)} />
          </Col>
        </Row>
        <Form.Group className="mb-2">
          <Form.Label className="small">Mô tả ngữ cảnh</Form.Label>
          <Form.Control as="textarea" rows={2} value={body.context_description}
            onChange={(e) => set('context_description', e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label className="small">Vibe (tùy chọn)</Form.Label>
          <Form.Control size="sm" placeholder="vd: hào phóng, cân bằng, thận trọng..."
            value={body.vibe} onChange={(e) => set('vibe', e.target.value)} />
        </Form.Group>
        <div className="text-end mb-3">
          <Button variant="primary" size="sm" onClick={handleSuggest} disabled={loading}>
            {loading ? <Spinner size="sm" className="me-2" /> : <Sparkles size={14} className="me-2" />}
            Gợi ý
          </Button>
        </div>
        {result && (
          <>
            <Alert variant={result.provider === 'preset' ? 'warning' : 'success'} className="py-2 mb-2">
              Provider: <strong>{result.provider}</strong>
              {result.expected_mean != null && <> · Mean: <strong>{Number(result.expected_mean).toFixed(2)}</strong></>}
            </Alert>
            <TierEditor tiers={result.tiers || []} onChange={(t) => setResult({ ...result, tiers: t })} />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Đóng</Button>
        <Button variant="success" disabled={!result?.tiers?.length}
          onClick={() => { onApply(result.tiers); onHide(); }}>
          Áp dụng tiers vào form
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Simulate Modal
// ─────────────────────────────────────────────────────────────────────────────
function SimulateModal({ show, onHide, distribution, tiers }) {
  const [iterations, setIterations] = useState(1000);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleRun = async () => {
    setLoading(true);
    try {
      let res;
      if (distribution?.id) {
        res = await randomRewardAPI.simulate(distribution.id, { iterations: Number(iterations) });
      } else {
        res = await randomRewardAPI.simulateAdHoc({ tiers, iterations: Number(iterations) });
      }
      setResult(res);
    } catch (e) {
      toast.error(e?.message || 'Simulate lỗi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="md" centered>
      <Modal.Header closeButton>
        <Modal.Title><BarChart size={18} className="me-2" />Simulate</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <InputGroup className="mb-3">
          <InputGroup.Text>Iterations</InputGroup.Text>
          <Form.Control type="number" min={100} max={100000} value={iterations}
            onChange={(e) => setIterations(e.target.value)} />
          <Button onClick={handleRun} disabled={loading}>
            {loading ? <Spinner size="sm" /> : 'Chạy'}
          </Button>
        </InputGroup>
        {result && (
          <>
            <div className="mb-2 text-muted small">
              Empirical mean: <strong>{Number(result.empirical_mean ?? 0).toFixed(2)}</strong>
              {result.theoretical_mean != null && (
                <> · Theoretical: <strong>{Number(result.theoretical_mean).toFixed(2)}</strong></>
              )}
            </div>
            <Table size="sm">
              <thead><tr><th>Tier</th><th>Label</th><th>Hits</th><th>Emp %</th><th>Theo %</th></tr></thead>
              <tbody>
                {(result.by_tier || []).map((row) => (
                  <tr key={row.tier_id}>
                    <td><code>{row.tier_id}</code></td>
                    <td>{row.label}</td>
                    <td>{row.count}</td>
                    <td>{((row.empirical_prob ?? 0) * 100).toFixed(2)}%</td>
                    <td className="text-muted">{((row.theoretical_prob ?? 0) * 100).toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Distribution Form Modal
// ─────────────────────────────────────────────────────────────────────────────
function DistributionFormModal({ show, onHide, distribution, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const [showSim, setShowSim] = useState(false);

  useEffect(() => {
    if (!show) return;
    if (distribution) {
      setForm({
        context_key: distribution.context_key,
        name: distribution.name,
        description: distribution.description || '',
        tiers: (distribution.tiers || []).map((t) => ({ ...t })),
        expected_mean: distribution.expected_mean,
        hard_min: distribution.hard_min,
        hard_max: distribution.hard_max,
        daily_budget: distribution.daily_budget,
        high_tier_caps: distribution.high_tier_caps || [],
        is_active: distribution.is_active,
      });
    } else {
      setForm({ ...EMPTY_FORM, tiers: [newTier(), newTier(), newTier()] });
    }
  }, [show, distribution]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.context_key.trim() || !form.name.trim()) {
      toast.error('Cần nhập context_key và tên');
      return;
    }
    if (!form.tiers.length) {
      toast.error('Cần ít nhất 1 tier');
      return;
    }
    const payload = {
      context_key: form.context_key.trim(),
      name: form.name.trim(),
      description: form.description?.trim() || undefined,
      tiers: form.tiers,
      expected_mean: form.expected_mean != null && form.expected_mean !== '' ? Number(form.expected_mean) : undefined,
      hard_min: form.hard_min != null && form.hard_min !== '' ? Number(form.hard_min) : undefined,
      hard_max: form.hard_max != null && form.hard_max !== '' ? Number(form.hard_max) : undefined,
      daily_budget: form.daily_budget != null && form.daily_budget !== '' ? Number(form.daily_budget) : undefined,
      high_tier_caps: form.high_tier_caps?.length ? form.high_tier_caps : undefined,
      is_active: !!form.is_active,
    };
    setSaving(true);
    try {
      if (distribution) {
        await randomRewardAPI.update(distribution.id, payload);
        toast.success('Đã cập nhật distribution');
      } else {
        await randomRewardAPI.create(payload);
        toast.success('Đã tạo distribution');
      }
      onSaved();
      onHide();
    } catch (err) {
      toast.error(err?.message || 'Lỗi lưu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{distribution ? 'Chỉnh sửa Distribution' : 'Tạo Distribution mới'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Label className="small fw-semibold">context_key <span className="text-danger">*</span></Form.Label>
                <Form.Control value={form.context_key}
                  onChange={(e) => set('context_key', e.target.value)}
                  placeholder="discount.<programId> hoặc employee.bonus.shift"
                  style={{ fontFamily: 'monospace' }}
                  disabled={!!distribution}
                />
                <Form.Text className="text-muted">Khoá namespace duy nhất, không đổi được sau khi tạo</Form.Text>
              </Col>
              <Col md={6}>
                <Form.Label className="small fw-semibold">Tên <span className="text-danger">*</span></Form.Label>
                <Form.Control value={form.name} onChange={(e) => set('name', e.target.value)}
                  placeholder="Ví dụ: Random discount Google Maps review" />
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Mô tả</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.description}
                onChange={(e) => set('description', e.target.value)} />
            </Form.Group>

            <Row className="g-3 mb-3">
              <Col md={3}>
                <Form.Label className="small">Hard min</Form.Label>
                <Form.Control type="number" value={form.hard_min ?? ''}
                  onChange={(e) => set('hard_min', e.target.value)} />
              </Col>
              <Col md={3}>
                <Form.Label className="small">Hard max</Form.Label>
                <Form.Control type="number" value={form.hard_max ?? ''}
                  onChange={(e) => set('hard_max', e.target.value)} />
              </Col>
              <Col md={3}>
                <Form.Label className="small">Mean mục tiêu</Form.Label>
                <Form.Control type="number" step="0.01" value={form.expected_mean ?? ''}
                  onChange={(e) => set('expected_mean', e.target.value)} />
              </Col>
              <Col md={3}>
                <Form.Label className="small">Daily budget (VND)</Form.Label>
                <Form.Control type="number" value={form.daily_budget ?? ''}
                  onChange={(e) => set('daily_budget', e.target.value)}
                  placeholder="Trống = không giới hạn" />
              </Col>
            </Row>

            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0 fw-semibold">Tiers</h6>
              <div className="d-flex gap-2">
                <Button size="sm" variant="outline-primary" onClick={() => setShowAi(true)} type="button">
                  <Sparkles size={14} className="me-1" /> AI gợi ý
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => setShowSim(true)} type="button">
                  <BarChart size={14} className="me-1" /> Simulate
                </Button>
              </div>
            </div>
            <TierEditor tiers={form.tiers} onChange={(tiers) => set('tiers', tiers)} />

            <Form.Check
              className="mt-3"
              type="switch"
              label="Active"
              checked={!!form.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Hủy</Button>
            <Button type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-2" /> : null}
              {distribution ? 'Cập nhật' : 'Tạo mới'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <AiSuggestModal show={showAi} onHide={() => setShowAi(false)}
        onApply={(tiers) => set('tiers', tiers.map((t) => ({ ...newTier(t.id), ...t })))} />

      <SimulateModal show={showSim} onHide={() => setShowSim(false)}
        distribution={distribution} tiers={form.tiers} />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Audit Tab
// ─────────────────────────────────────────────────────────────────────────────
function AuditTab({ distributions }) {
  const [selectedId, setSelectedId] = useState('');
  const [rolls, setRolls] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (distributions.length && !selectedId) setSelectedId(distributions[0].id);
  }, [distributions, selectedId]);

  const load = useCallback(async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      const res = await randomRewardAPI.audit(selectedId, { limit: 50 });
      setRolls(res?.data ?? res ?? []);
    } catch (e) {
      toast.error(e?.message || 'Lỗi tải audit');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="border-0 shadow-sm" style={{ borderRadius: 14 }}>
      <Card.Body>
        <Row className="g-2 mb-3 align-items-center">
          <Col md={6}>
            <Form.Select size="sm" value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
              <option value="">— Chọn distribution —</option>
              {distributions.map((d) => (
                <option key={d.id} value={d.id}>{d.name} ({d.context_key})</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Button size="sm" variant="outline-secondary" onClick={load}>
              <ArrowRepeat size={14} className="me-1" /> Tải lại
            </Button>
          </Col>
        </Row>
        {loading ? <Spinner /> : (
          <Table size="sm" responsive hover style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Thời điểm</th><th>Tier</th><th>Reward</th>
                <th>scope_key</th><th>Branch</th>
              </tr>
            </thead>
            <tbody>
              {rolls.length === 0 && (
                <tr><td colSpan={5} className="text-center text-muted py-3">Chưa có roll</td></tr>
              )}
              {rolls.map((r) => {
                const snap = r.reward_snapshot || {};
                return (
                  <tr key={r.id}>
                    <td>{timeAgo(r.created_at)}</td>
                    <td><Badge bg="info">{r.tier_id}</Badge></td>
                    <td>{snap.value} {snap.type === 'PERCENTAGE' ? '%' : snap.type === 'FIXED_AMOUNT' ? 'đ' : 'pts'}</td>
                    <td><code style={{ fontSize: 11 }}>{r.scope_key}</code></td>
                    <td>{r.branch_id || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard Tab
// ─────────────────────────────────────────────────────────────────────────────
function DashboardTab({ distributions }) {
  const total = distributions.length;
  const active = distributions.filter((d) => d.is_active).length;
  const aiGen = distributions.filter((d) => d.ai_generated).length;
  const [days, setDays] = useState(7);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const to = new Date();
      const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
      const res = await randomRewardAPI.dashboard({
        from: from.toISOString(),
        to: to.toISOString(),
      });
      setStats(res?.data ?? res ?? []);
    } catch (e) {
      toast.error(e?.message || 'Lỗi tải stats');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  return (
    <Row className="g-3">
      <Col md={4}>
        <Card className="border-0 shadow-sm"><Card.Body>
          <div className="text-muted small">Tổng distributions</div>
          <div className="fw-bold fs-3">{total}</div>
        </Card.Body></Card>
      </Col>
      <Col md={4}>
        <Card className="border-0 shadow-sm"><Card.Body>
          <div className="text-muted small">Đang active</div>
          <div className="fw-bold fs-3 text-success">{active}</div>
        </Card.Body></Card>
      </Col>
      <Col md={4}>
        <Card className="border-0 shadow-sm"><Card.Body>
          <div className="text-muted small">AI sinh</div>
          <div className="fw-bold fs-3 text-primary">{aiGen}</div>
        </Card.Body></Card>
      </Col>
      <Col md={12}>
        <Card className="border-0 shadow-sm"><Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-semibold">Roll stats {days} ngày gần nhất</div>
            <InputGroup size="sm" style={{ width: 180 }}>
              <InputGroup.Text>Days</InputGroup.Text>
              <Form.Control type="number" min={1} max={90} value={days}
                onChange={(e) => setDays(Number(e.target.value) || 7)} />
            </InputGroup>
          </div>
          {loading ? <Spinner size="sm" /> : (
            <Table size="sm" responsive>
              <thead>
                <tr>
                  <th>Distribution</th><th>context_key</th>
                  <th>Total rolls</th><th>Total value</th><th>Top tiers</th>
                </tr>
              </thead>
              <tbody>
                {stats.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted py-3">
                    Chưa có roll trong khoảng này
                  </td></tr>
                )}
                {stats.map((row) => (
                  <tr key={row.distribution_id}>
                    <td>{row.name}</td>
                    <td><code style={{ fontSize: 11 }}>{row.context_key}</code></td>
                    <td><strong>{row.total_rolls}</strong></td>
                    <td>{Number(row.total_value).toLocaleString('vi-VN')}</td>
                    <td>
                      {(row.by_tier || []).slice(0, 3).map((t) => (
                        <Badge key={t.tier_id} bg="info" className="me-1">
                          {t.tier_id} × {t.count}
                        </Badge>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body></Card>
      </Col>
      <Col md={12}>
        <Card className="border-0 shadow-sm"><Card.Body>
          <div className="fw-semibold mb-2">Mean kỳ vọng theo distribution</div>
          <Table size="sm">
            <thead><tr><th>Tên</th><th>context_key</th><th>Mean</th><th>Tier count</th></tr></thead>
            <tbody>
              {distributions.map((d) => (
                <tr key={d.id}>
                  <td>{d.name}</td>
                  <td><code style={{ fontSize: 11 }}>{d.context_key}</code></td>
                  <td>{d.expected_mean != null ? Number(d.expected_mean).toFixed(2) : '—'}</td>
                  <td>{(d.tiers || []).length}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body></Card>
      </Col>
    </Row>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
export default function RandomRewardPage() {
  const [tab, setTab] = useState('distributions');
  const [distributions, setDistributions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await randomRewardAPI.list({ limit: 100 });
      setDistributions(res?.data ?? res ?? []);
    } catch (e) {
      toast.error(e?.message || 'Lỗi tải distributions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleEdit = (d) => { setEditing(d); setShowForm(true); };
  const handleNew = () => { setEditing(null); setShowForm(true); };
  const handleDelete = async (d) => {
    if (!window.confirm(`Soft delete "${d.name}"?`)) return;
    try {
      await randomRewardAPI.remove(d.id);
      toast.success('Đã xoá');
      load();
    } catch (e) {
      toast.error(e?.message || 'Lỗi xoá');
    }
  };

  return (
    <PageLayout>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="fw-bold mb-0">
            <Stars size={22} className="me-2" style={{ color: '#7c3aed' }} />
            Random Reward Engine
          </h4>
          <small className="text-muted">Cấu hình bảng phân phối phần thưởng ngẫu nhiên có trọng số</small>
        </div>
        <Button onClick={handleNew} style={{ background: '#7c3aed', border: 'none' }}>
          <Plus size={16} className="me-1" /> Tạo Distribution
        </Button>
      </div>

      <Tab.Container activeKey={tab} onSelect={setTab}>
        <Nav variant="pills" className="mb-3 gap-2">
          <Nav.Item><Nav.Link eventKey="distributions"><ListUl size={14} className="me-1" />Distributions</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="audit">Audit Log</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="dashboard"><BarChart size={14} className="me-1" />Dashboard</Nav.Link></Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="distributions">
            {loading ? <Spinner /> : (
              <Row className="g-3">
                {distributions.length === 0 && (
                  <Col md={12}><Alert variant="light" className="text-center text-muted">
                    Chưa có distribution nào. Bấm "Tạo Distribution" để bắt đầu.
                  </Alert></Col>
                )}
                {distributions.map((d) => (
                  <Col md={6} key={d.id}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="fw-bold">{d.name}</div>
                            <code style={{ fontSize: 11 }}>{d.context_key}</code>
                          </div>
                          <div className="d-flex gap-1">
                            {d.ai_generated && <Badge bg="primary"><Sparkles size={10} /> AI</Badge>}
                            <Badge bg={d.is_active ? 'success' : 'secondary'}>
                              {d.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </div>
                        {d.description && <div className="text-muted small mt-2">{d.description}</div>}
                        <div className="mt-2 d-flex flex-wrap gap-2 small">
                          <Badge bg="light" text="dark">{(d.tiers || []).length} tiers</Badge>
                          {d.expected_mean != null && (
                            <Badge bg="light" text="dark">Mean {Number(d.expected_mean).toFixed(2)}</Badge>
                          )}
                          {d.daily_budget && (
                            <Badge bg="light" text="dark">Budget {formatVND(d.daily_budget)}đ/ngày</Badge>
                          )}
                        </div>
                        <div className="mt-3 d-flex gap-2">
                          <Button size="sm" variant="outline-secondary" onClick={() => handleEdit(d)}>
                            <PencilSquare size={12} className="me-1" />Sửa
                          </Button>
                          <Button size="sm" variant="outline-danger" onClick={() => handleDelete(d)}>
                            <Trash size={12} className="me-1" />Xoá
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Tab.Pane>
          <Tab.Pane eventKey="audit"><AuditTab distributions={distributions} /></Tab.Pane>
          <Tab.Pane eventKey="dashboard"><DashboardTab distributions={distributions} /></Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      <DistributionFormModal show={showForm}
        onHide={() => setShowForm(false)}
        distribution={editing}
        onSaved={load} />
    </PageLayout>
  );
}
